import { useEffect, useState } from "react";
import { useOutletContext, Link as RouterLink } from "react-router-dom";
import Particles from "../Background/Particles";
import { Fade } from "react-awesome-reveal";
import type { PortfolioSiteData } from "../types/resume";
import articlesRaw from "../data/articles.json";
import GitHubActivity from "./Component/GitHubActivity";
import Seo from "../components/Seo";

const COUNT_UP_DURATION = 1400;
const HIGHLIGHT_REVEAL_DELAY = 450;
const STAGGER_DELAY = 150;
const ARTICLE_PREVIEW_LIMIT = 2;

interface ArticlePreview {
  platform: string;
  icon: string;
  color: string;
  title: string;
  desc: string;
  url: string;
  date: string;
}

const HIGHLIGHTS = [
  { value: "10+", label: "Projects" },
  { value: "2+", label: "Years of Experience" },
  { value: "100%", label: "Responsive" },
];

const TECH_STACK = [
  { name: "HTML5", image: "/image/tech/html5.svg" },
  { name: "CSS3", image: "/image/tech/css3.svg" },
  { name: "JavaScript", image: "/image/tech/javascript.svg" },
  { name: "TypeScript", image: "/image/tech/typescript.svg" },
  { name: "React", image: "/image/tech/react.svg" },
  { name: "Next.js", image: "/image/tech/nextdotjs.svg" },
  { name: "Tailwind CSS", image: "/image/tech/tailwindcss.svg" },
  { name: "Laravel", image: "/image/tech/laravel.svg" },
  { name: "Git", image: "/image/tech/git.svg" },
  { name: "GitHub", image: "/image/tech/github.svg" },
  { name: "Figma", image: "/image/tech/figma.svg" },
  { name: "Vercel", image: "/image/tech/vercel.svg" },
];

const latestArticles = [...(articlesRaw as ArticlePreview[])]
  .sort((first, second) => new Date(second.date).getTime() - new Date(first.date).getTime())
  .slice(0, ARTICLE_PREVIEW_LIMIT);

const CountUp = ({ value, delay = 0 }: { value: string; delay?: number }) => {
  const target = Number(value.match(/\d+/)?.[0] ?? 0);
  const suffix = value.replace(/\d+/g, "");
  const [count, setCount] = useState(0);

  useEffect(() => {
    let animationFrame: number;
    let timeout: ReturnType<typeof setTimeout>;

    const startCount = () => {
      const startTime = performance.now();

      const updateCount = (time: number) => {
        const progress = Math.min((time - startTime) / COUNT_UP_DURATION, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(target * easedProgress));

        if (progress < 1) animationFrame = requestAnimationFrame(updateCount);
      };

      animationFrame = requestAnimationFrame(updateCount);
    };

    timeout = setTimeout(startCount, delay);
    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(animationFrame);
    };
  }, [delay, target]);

  return <>{count}{suffix}</>;
};

const Overview = () => {
  // Safer access to context data to prevent crashes if the context is not available.
  const context = useOutletContext<{ data: Partial<PortfolioSiteData> }>();
  const mainData = context?.data?.main;
  const projectPreviews = context?.data?.portfolio?.projects?.slice(0, 2) ?? [];
  const certificatePreviews = context?.data?.certificates?.slice(0, 3) ?? [];

  if (!mainData) {
    return (
      <div className="flex min-h-screen items-center justify-center text-center text-slate-400">
        Waiting for main data...
      </div>
    );
  }

  const { name, description, project } = mainData;
  return (
    <>
      <Seo
        title="Efwan Rizaldi — Software Engineer | Web3 & Blockchain"
        description="Software Engineer focused on frontend, Web3 & Blockchain. Open to remote / international opportunities."
        path="/"
      />
      <section
        id="overview"
        className="overview-grid relative min-h-screen overflow-hidden pt-[80px] text-center"
      >
      <div className="absolute top-0 left-0 h-full w-full opacity-40">
        <Particles
          particleColors={["#ffffff"]}
          particleCount={80}
          particleSpread={20}
          speed={0.05}
          particleBaseSize={40}
          alphaParticles={false}
          disableRotation={true}
          pixelRatio={1}
        />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1600px] px-5 py-10 sm:px-8 sm:py-12 lg:px-10 lg:py-14">
        <div className="mx-auto flex min-h-[560px] w-full max-w-[1500px] flex-col items-center justify-start pt-[96px] text-center sm:min-h-[650px] sm:pt-[120px] lg:pt-[136px]">
          <Fade direction="up" duration={700} triggerOnce>
            <div className="flex items-center gap-4 font-mono text-2xl font-semibold uppercase tracking-[0.18em] text-orange-300 sm:text-3xl md:text-4xl">
              <span className="h-px w-8 bg-orange-400 sm:w-12 md:w-16" />
              <p>Portfolio / 2026</p>
              <span className="h-px w-8 bg-orange-400 sm:w-12 md:w-16" />
            </div>
          </Fade>

          <Fade direction="up" delay={150} duration={900} triggerOnce>
            <h1 className="mt-6 text-[clamp(1.7rem,9vw,10rem)] font-extrabold leading-[0.92] tracking-[-0.06em] text-slate-50">
              <span className="block whitespace-nowrap">Building thoughtful</span>
              <span className="block whitespace-nowrap bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300 bg-clip-text text-transparent">
                digital experiences.
              </span>
            </h1>
          </Fade>

          <Fade
            direction="up"
            delay={450}
            duration={700}
            triggerOnce
            style={{ pointerEvents: "auto" }}
          >
            <div className="mt-10 flex w-full flex-col justify-center gap-3 sm:w-auto sm:flex-row">
              <RouterLink
                to="/portfolio"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 px-8 py-4 font-semibold text-white shadow-lg shadow-orange-500/20 transition duration-300 hover:-translate-y-0.5 hover:bg-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-[#050505]"
              >
                View selected work
                <i className="fa-solid fa-arrow-right text-xs" />
              </RouterLink>
              {project && (
                <a
                  href={project}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-8 py-4 font-semibold text-slate-200 transition duration-300 hover:-translate-y-0.5 hover:border-slate-500 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-[#050505]"
                >
                  <i className="fa-brands fa-github" />
                  Source code
                </a>
              )}
              <RouterLink
                to="/certificates"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-8 py-4 font-semibold text-slate-200 transition duration-300 hover:-translate-y-0.5 hover:border-slate-500 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-[#050505]"
              >
                <i className="fa-solid fa-envelope" />
                Certificates
              </RouterLink>
            </div>
          </Fade>


          <div className="mt-14 grid w-full grid-cols-1 gap-3 text-center sm:mt-16 sm:grid-cols-3 sm:gap-5">
            {HIGHLIGHTS.map((highlight, index) => {
              const delay = HIGHLIGHT_REVEAL_DELAY + index * STAGGER_DELAY;

              return (
                <Fade key={highlight.label} direction="up" delay={delay} duration={650} triggerOnce>
                  <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-7 backdrop-blur-sm transition duration-300 hover:border-orange-500/40 hover:bg-slate-900/80 sm:p-8">
                    <p className="font-mono text-5xl font-bold tracking-tight text-orange-400 sm:text-6xl lg:text-7xl">
                      <CountUp value={highlight.value} delay={delay} />
                    </p>
                    <p className="mt-2 font-mono text-xs uppercase tracking-wider text-slate-400">
                      {highlight.label}
                    </p>
                  </div>
                </Fade>
              );
            })}
          </div>
        </div>

        <section className="mt-20 text-center sm:mt-28">
          <Fade direction="up" delay={300} duration={800} triggerOnce>
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900/50 p-10 backdrop-blur-sm sm:p-16">
              <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-orange-500/10 via-transparent to-cyan-400/10 blur-3xl" />
              <h2 className="text-[clamp(3rem,7vw,7rem)] font-extrabold leading-[0.9] tracking-[-0.06em]">
                <span className="bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300 bg-clip-text text-transparent">
                  {name}.
                </span>
              </h2>
              <p className="mx-auto mt-7 max-w-4xl text-lg leading-relaxed text-slate-400 sm:text-xl md:text-2xl">
                {description}
              </p>
            </div>
          </Fade>
        </section>

        <section className="border-t border-white/10 pt-20 text-left sm:pt-28">
          <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-5 py-2.5 font-mono text-sm text-cyan-300">
                <i className="fa-solid fa-pen-nib" /> Latest insights
              </span>
              <h2 className="mt-7 text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
                Latest notes from the <span className="text-orange-500">community.</span>
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
                A quick selection of the writing I share. Visit the Articles page to read everything.
              </p>
            </div>
            <RouterLink
              to="/artikel"
              className="inline-flex w-fit items-center gap-2 rounded-full border border-orange-400/40 bg-orange-500/10 px-6 py-3 font-semibold text-orange-300 transition hover:-translate-y-0.5 hover:bg-orange-500 hover:text-white"
            >
              View all articles <i className="fa-solid fa-arrow-right text-xs" />
            </RouterLink>
          </div>

          <div className="mx-auto mt-10 grid w-full max-w-[1500px] gap-5 md:grid-cols-2">
            {latestArticles.map((article, index) => (
              <Fade key={article.url} direction="up" delay={index * 120} duration={600} triggerOnce>
                <article className="group h-full rounded-3xl border border-white/10 bg-slate-900/55 p-6 backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:border-cyan-400/40 hover:bg-slate-900/80 sm:p-7">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/5">
                        <i className={`fab ${article.icon} text-xl ${article.color}`} aria-hidden="true" />
                      </span>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-orange-300">{article.platform}</p>
                        <time className="mt-1 block text-xs text-slate-500">{article.date}</time>
                      </div>
                    </div>
                    <i className="fa-solid fa-arrow-up-right-from-square mt-1 text-sm text-cyan-300 opacity-0 transition group-hover:opacity-100" aria-hidden="true" />
                  </div>
                  <h3 className="mt-6 text-xl font-bold leading-snug text-white sm:text-2xl">{article.title}</h3>
                  <p className="mt-3 line-clamp-2 text-sm leading-6 text-slate-400 sm:text-base">{article.desc}</p>
                  <a href={article.url} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex text-sm font-semibold text-cyan-300 transition hover:text-cyan-100">
                    Read article <span className="ml-2">→</span>
                  </a>
                </article>
              </Fade>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-12 w-full max-w-[1500px] sm:mt-16">
          <GitHubActivity />
        </section>

        <section className="border-t border-white/10 pt-20 text-left sm:pt-28">
          <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-500/10 px-5 py-2.5 font-mono text-sm text-orange-300">
                <i className="fa-solid fa-grid-2" /> Selected work
              </span>
              <h2 className="mt-7 text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
                Selected project <span className="text-orange-500">preview.</span>
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
                A selection of products I designed and built, from user experience to technical implementation.
              </p>
            </div>
            <RouterLink to="/portfolio" className="inline-flex w-fit items-center gap-2 rounded-full border border-orange-400/40 bg-orange-500/10 px-6 py-3 font-semibold text-orange-300 transition hover:-translate-y-0.5 hover:bg-orange-500 hover:text-white">
              View all projects <i className="fa-solid fa-arrow-right text-xs" />
            </RouterLink>
          </div>

          <div className="mx-auto mt-10 grid w-full max-w-[1500px] gap-5 md:grid-cols-2">
            {projectPreviews.map((projectItem, index) => (
              <Fade key={projectItem.title} direction="up" delay={index * 120} duration={600} triggerOnce>
                <a href={projectItem.url} target="_blank" rel="noopener noreferrer" className="group block h-full overflow-hidden rounded-3xl border border-white/10 bg-slate-900/55 backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:border-orange-400/50 hover:bg-slate-900/80">
                  <div className="relative aspect-[16/8] overflow-hidden bg-slate-800">
                    <img
                      src={`/image/portfolio/${projectItem.image}`}
                      alt=""
                      width={960}
                      height={480}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
                    <span className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-slate-950/80 text-orange-300 opacity-0 backdrop-blur transition group-hover:opacity-100"><i className="fa-solid fa-arrow-up-right-from-square text-sm" /></span>
                  </div>
                  <div className="p-6 sm:p-7">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-orange-300">{projectItem.category}</p>
                    <h3 className="mt-3 text-2xl font-bold text-white">{projectItem.title}</h3>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {projectItem.techStack?.slice(0, 4).map((tech) => <span key={tech} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-slate-300">{tech}</span>)}
                    </div>
                  </div>
                </a>
              </Fade>
            ))}
          </div>
        </section>

        <section className="border-t border-white/10 pt-20 text-left sm:pt-28">
          <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-5 py-2.5 font-mono text-sm text-cyan-300">
                <i className="fa-solid fa-certificate" /> Credentials
              </span>
              <h2 className="mt-7 text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
                Latest <span className="text-cyan-300">certificates.</span>
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
                A summary of the learning and skill validation I continue to develop.
              </p>
            </div>
            <RouterLink to="/certificates"  className="inline-flex w-fit items-center gap-2 rounded-full border border-orange-400/40 bg-orange-500/10 px-6 py-3 font-semibold text-orange-300 transition hover:-translate-y-0.5 hover:bg-orange-500 hover:text-white">
              View all certificates <i className="fa-solid fa-arrow-right text-xs" />
            </RouterLink>
          </div>

          <div className="mx-auto mt-10 grid w-full max-w-[1500px] gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {certificatePreviews.map((certificate, index) => (
              <Fade key={certificate.link} direction="up" delay={index * 120} duration={600} triggerOnce>
                <a href={certificate.link} target="_blank" rel="noopener noreferrer" className="group block h-full overflow-hidden rounded-3xl border border-white/10 bg-slate-900/55 backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:border-cyan-300/50 hover:bg-slate-900/80">
                  <div className="aspect-[16/10] overflow-hidden bg-slate-800 p-3">
                    <img
                      src={`/${certificate.image}`}
                      alt=""
                      width={640}
                      height={400}
                      className="h-full w-full rounded-2xl object-cover transition duration-500 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-300">{certificate.issuer}</p>
                    <h3 className="mt-3 text-xl font-bold leading-snug text-white">{certificate.title}</h3>
                    <p className="mt-3 text-sm text-slate-400">{certificate.date}</p>
                  </div>
                </a>
              </Fade>
            ))}
          </div>
        </section>

        <section className="border-t border-white/10 pt-20 text-left sm:pt-28">
          <div className="mx-auto w-full max-w-[1200px] text-center">
            <Fade direction="up" delay={150} duration={600} triggerOnce>
              <span className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-5 py-2.5 font-mono text-sm text-orange-400">
                <i className="fa-solid fa-wand-magic-sparkles" /> Frontend Technologies
              </span>
            </Fade>
            <Fade direction="up" delay={300} duration={700} triggerOnce>
              <h2 className="mt-8 font-mono text-5xl font-bold leading-[0.98] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-8xl">
                Technologies I <span className="text-orange-500">Work With</span>
              </h2>
            </Fade>
            <Fade direction="up" delay={450} duration={700} triggerOnce>
              <p className="mx-auto mt-7 max-w-3xl font-mono text-base leading-relaxed text-slate-400 sm:text-lg md:text-xl">
                Tools I use to build fast, modern, responsive, and accessible web applications.
              </p>
            </Fade>
          </div>

          <div className="mx-auto mt-16 grid w-full max-w-[1500px] grid-cols-2 gap-4 sm:mt-20 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 xl:grid-cols-6">
            {TECH_STACK.map((tech, index) => (
              <div
                key={tech.name}
                className="group relative flex min-h-40 flex-col items-center justify-center overflow-hidden rounded-3xl border border-white/10 bg-slate-900/60 p-5 backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:border-orange-500/70 hover:bg-slate-900 hover:shadow-lg hover:shadow-orange-950/30 sm:min-h-48 sm:p-6"
              >
                <span className="absolute right-4 top-4 font-mono text-xs text-slate-600 transition-colors duration-300 group-hover:text-orange-400">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <img
                  src={tech.image}
                  alt=""
                  width={56}
                  height={56}
                  className="h-14 w-14 object-contain transition-transform duration-300 group-hover:scale-110 sm:h-16 sm:w-16"
                  loading="lazy"
                />
                <p className="mt-5 font-mono text-base font-semibold text-slate-200 sm:text-lg">{tech.name}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </section>
    </>
  );
};

export default Overview;
